import { adjustToCurrentMonth } from 'components/atoms/base-range-datepicker';
import { endOfDay, startOfDay } from 'date-fns';
import dayjs from 'dayjs';
import { PresetKeyEnum, timePresetKeys } from 'infrastructure/enums';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import s from './styles.module.scss';

import type { TOverviewDashboardFilter } from 'infrastructure/hooks/rpm/overview-dashboard';
import type { Presets } from 'components/atoms/base-range-datepicker';

export const useRangePicker = (
  updateFilter: (newFilter: Partial<TOverviewDashboardFilter>) => void,
) => {
  const { t } = useTranslation();

  const onDateChange = async (
    dates: [Date | null, Date | null],
    dateString: [string, string],
    info: { range?: 'start' | 'end' },
    presetKey?: string,
  ) => {
    const [startDate, endDate] = dates;

    if (startDate === null && endDate === null) {
      updateFilter({
        startTime: undefined,
        endTime: undefined,
        page: undefined,
      });
      return;
    }

    const isTimePreset =
      presetKey && timePresetKeys.includes(presetKey as PresetKeyEnum);

    if (startDate && endDate) {
      updateFilter({
        startTime: (isTimePreset ? startDate : startOfDay(startDate)).getTime(),
        endTime: (isTimePreset ? endDate : endOfDay(endDate)).getTime(),
        page: undefined,
      });
    } else {
      if (startDate && info.range === 'start') {
        updateFilter({
          startTime: (isTimePreset
            ? startDate
            : startOfDay(startDate)
          ).getTime(),
          page: undefined,
        });
      }
      if (endDate && info.range === 'end') {
        updateFilter({
          endTime: (isTimePreset ? endDate : endOfDay(endDate)).getTime(),
          page: undefined,
        });
      }
    }
  };

  const excludePresetsKeys = [
    PresetKeyEnum.LastFortyFiveDays,
    PresetKeyEnum.LastSevenDays,
    PresetKeyEnum.LastThirtyDays,
    PresetKeyEnum.LastFourteenDays,
    PresetKeyEnum.LastTwentyFourDays,
  ];
  const checkDisabledPreset = useCallback((presets: Presets) => {
    return presets.map((preset) => {
      const isRangeInDifferentMonth =
        preset.value?.[0].getMonth() !== preset.value?.[1].getMonth();

      if (isRangeInDifferentMonth) {
        return {
          ...preset,
          value: undefined,
          label: React.isValidElement<HTMLSpanElement>(preset.label)
            ? React.cloneElement(preset.label, {
                className: s['disabled-preset'],
              })
            : preset.label,
        };
      }

      return preset;
    });
  }, []);

  const dateRanges: Presets = checkDisabledPreset([
    {
      label: (
        <span data-cy="ranges">
          {t('dates.lastCountHours', {
            count: 6,
          })}
        </span>
      ),
      value: adjustToCurrentMonth(dayjs().add(-6, 'h'), dayjs(), false),
      key: PresetKeyEnum.LastSixHours,
      index: 0.06,
    },
    {
      label: (
        <span data-cy="ranges">
          {t('dates.lastCountHours', {
            count: 8,
          })}
        </span>
      ),
      value: adjustToCurrentMonth(dayjs().add(-8, 'h'), dayjs(), false),
      key: PresetKeyEnum.LastEightHours,
      index: 0.08,
    },
  ]);

  const getPresets = useCallback((presets: Presets) => {
    return [
      ...dateRanges,
      ...presets.filter(
        (preset) => !excludePresetsKeys.includes(preset.key as PresetKeyEnum),
      ),
    ];
  }, []);

  return {
    onDateChange,
    getPresets,
  };
};
